// The places lab's own light (`?scene=places`, gate C4): the lab has no clock, so it derives an indoor light model, or
// its night (`?night`), from the outdoor scene look and a row of the places-light table, and blends between looks
// itself. The streamed valley (the default scene) takes its rooms' light from the sky stream's look instead
// (`look.indoor(profile)` through src/sky/view.ts, phase C joined); this file was the places stream's stand-in for it
// and stays as the lab's. The view only calls `indoor`, `night` and `blendLooks`, and reads a row's `night` for the
// kit's lit faces.
import * as THREE from "three";
import type { LowPolySceneLook } from "@rpg/look";
import type { PlacesLightRow } from "./content";

/** The outdoor look changed by a light row: the sun scaled, the fill and maybe the sky replaced, the haze thinned. */
export function indoor(base: LowPolySceneLook, row: PlacesLightRow): LowPolySceneLook {
  return {
    ...base,
    sky: row.sky ? { ...row.sky } : { ...base.sky },
    haze: { ...base.haze, density: base.haze.density * row.haze },
    sun: { ...base.sun, intensity: base.sun.intensity * row.sun },
    fill: { ...row.fill },
    exposure: row.exposure,
    glow: row.glow,
  };
}

/** The lab's night: the same change from the night row (the sky stream's look.at(night) replaces it). */
export const night = indoor;

const mixHex = (a: string, b: string, t: number): string => `#${new THREE.Color(a).lerp(new THREE.Color(b), t).getHexString()}`;
const mix = (a: number, b: number, t: number): number => a + (b - a) * t;

/** A look part of the way from `a` to `b` (colours and numbers; the palette is `b`'s). */
export function blendLooks(a: LowPolySceneLook, b: LowPolySceneLook, t: number): LowPolySceneLook {
  if (t >= 1) return b;
  return {
    palette: b.palette,
    sky: { top: mixHex(a.sky.top, b.sky.top, t), horizon: mixHex(a.sky.horizon, b.sky.horizon, t), ground: mixHex(a.sky.ground, b.sky.ground, t) },
    haze: {
      near: mix(a.haze.near, b.haze.near, t),
      density: mix(a.haze.density, b.haze.density, t),
      base: mix(a.haze.base, b.haze.base, t),
      falloff: mix(a.haze.falloff, b.haze.falloff, t),
      height: mix(a.haze.height, b.haze.height, t),
      max: mix(a.haze.max, b.haze.max, t),
    },
    sun: { colour: mixHex(a.sun.colour, b.sun.colour, t), intensity: mix(a.sun.intensity, b.sun.intensity, t), elevation: mix(a.sun.elevation, b.sun.elevation, t), azimuth: mix(a.sun.azimuth, b.sun.azimuth, t) },
    fill: { sky: mixHex(a.fill.sky, b.fill.sky, t), ground: mixHex(a.fill.ground, b.fill.ground, t), intensity: mix(a.fill.intensity, b.fill.intensity, t) },
    exposure: mix(a.exposure, b.exposure, t),
    glow: mix(a.glow, b.glow, t),
  };
}
